import { CountryCode, GeoAutocompleteAddress, GeoLocationDetails } from '@brenger/api-client';
import { routePlannerClient, routePlannerClientToken as sessionToken } from './request';
import * as Sentry from '@sentry/browser';
export interface AddressInputOther {
  line1: string;
  line2?: string;
  postal_code?: string;
  locality: string;
  country_code: CountryCode;
}

export interface AddressInputNL {
  line1?: string;
  line2: string;
  postal_code: string;
  locality: string;
  country_code: 'NL';
}

export type AddressInput = AddressInputOther | AddressInputNL;

export const autocompleteAddress = async (a: AddressInput): Promise<GeoAutocompleteAddress> => {
  const span = Sentry.startInactiveSpan({
    name: 'address_lookup',
    op: 'function',
    attributes: Object.fromEntries(Object.entries(a).map(([key, value]) => [`input.${key}`, value])),
  });
  if (a.country_code === 'NL' && 'line2' in a) {
    span.setAttributes({
      type: 'NL',
    });
    return autocompleteAddressDutch(a, span);
  }
  span.setAttributes({
    type: 'other',
  });
  return autocompleteAddressOther(a as AddressInputOther, span);
};

/**
 * Dutch style lookups, by postal code and line2 / house number
 */
export const autocompleteAddressDutch = async (a: AddressInput, span: Sentry.Span): Promise<GeoAutocompleteAddress> => {
  // First attempt to autocomplete dutch style
  const dutchLookup = await routePlannerClient.geo.retrieveStructuredPostalCode({
    postal_code: a.postal_code as string,
    street_number: a.line2 as string,
    country: 'NL',
  });
  if (dutchLookup[0]) {
    const outcome = dutchLookup[0].address;
    // Not sure what is easy for filtering, so we set a success status and add success to attributes
    span.setStatus({
      code: 1,
      message: 'Success',
    });
    span.setAttributes({
      ...Object.fromEntries(Object.entries(outcome).map(([key, value]) => [`outcome.${key}`, value])),
      success: true,
    });
    if (a.locality && outcome.locality) {
      span.setAttributes({
        locality_change: a.locality !== outcome.locality,
      });
    }
    span.end();
    return outcome;
  }
  /**
   * We consider the ducth postal lookup as failed
   */
  span.setStatus({
    code: 2,
    message: 'Failed',
  });
  span.setAttributes({
    success: false,
  });
  span.end();

  const reboundSpan = Sentry.startInactiveSpan({
    name: 'address_lookup',
    op: 'function',
    attributes: {
      type: 'NL_rebound',
      ...Object.fromEntries(Object.entries(a).map(([key, value]) => [`input.${key}`, value])),
    },
  });
  // rebound to allow more free form search
  return autocompleteAddressOther(a as AddressInputOther, reboundSpan);
};
/**
 * All other countries, query style lookup
 */
export const autocompleteAddressOther = async (
  a: AddressInputOther,
  span: Sentry.Span
): Promise<GeoAutocompleteAddress> => {
  const line1line2 = [a.line1, a.line2].filter(Boolean).join(' ');
  const pcLocality = [a.postal_code, a.locality].filter(Boolean).join(' ');
  const query = `${line1line2}, ${pcLocality}`;
  const lookup = await routePlannerClient.geo.retrieveAutocomplete({ sessionToken, query });
  span.setAttributes({
    query,
    lookup_length: lookup.length,
  });
  if (lookup.length === 0) {
    span.setStatus({
      code: 2,
      message: 'Failed',
    });
    span.setAttributes({
      success: false,
    });
    span.end();
    throw new Error('No addresses are returned from autocomplete');
  }
  const results: GeoAutocompleteAddress[] = [];
  // Try each lookup result until we find one with all required fields
  for (const option of lookup) {
    const details = await routePlannerClient.geo.retrieveLocationDetails({
      sessionToken,
      placeId: option.place_id,
      query,
    });
    // Add all results to the array, so we can add them to span data later
    results.push(details.address);
    // We consider the result successful if it has all required fields are there, and the country matches
    if (
      details.address?.line1 &&
      details.address?.postal_code &&
      details.address?.locality &&
      a.country_code.toLowerCase() === details.address.country_code.toLowerCase()
    ) {
      const outcome = details.address;
      span.setStatus({
        code: 1,
        message: 'Success',
      });
      span.setAttributes({
        ...Object.fromEntries(Object.entries(outcome).map(([key, value]) => [`outcome.${key}`, value])),
        success: true,
      });
      if (a.locality && outcome.locality) {
        span.setAttributes({
          locality_change: a.locality !== outcome.locality,
        });
      }
      span.end();
      return outcome;
    }
  }
  // If we get here, no results had all requirments, so we do some administration and throw an error
  const attr: Record<string, string> = {};
  results.forEach((result, index) => {
    Object.entries(result).forEach(([key, value]) => {
      attr[`not_precise[${index}][${key}]`] = value;
    });
  });
  span.setStatus({
    code: 2,
    message: 'Failed',
  });
  span.setAttributes({
    success: false,
    ...attr,
  });
  span.end();
  throw new Error('Resolved addresses are not precise enough');
};

export const retrieveCachedLocationDetails = async (
  query: string,
  filter: string
): Promise<GeoLocationDetails | null> => {
  const storage = sessionStorage.getItem('brenger_geo_cache');
  const cache = storage ? (JSON.parse(storage) as { [key: string]: GeoLocationDetails }) : {};
  let result = undefined as GeoLocationDetails | undefined;
  if (Object.keys(cache).length > 0) {
    result = cache[query];
  }
  if (result) {
    return result;
  }
  const geo = await routePlannerClient.geo.retrieveAutocomplete({ sessionToken, query });
  if (geo.length === 0) {
    return null;
  }
  const geoFilteredResult = geo.find(filterResult => filterResult.label.includes(filter)) || geo[0];
  result = await routePlannerClient.geo.retrieveLocationDetails({
    sessionToken,
    placeId: geoFilteredResult.place_id,
    query,
  });
  if (!result) {
    return null;
  }
  sessionStorage.setItem(
    'brenger_geo_cache',
    JSON.stringify({
      ...cache,
      [query]: result,
    })
  );
  return result;
};
